import {
  createEnrichmentMiddleware,
  createErrorDedupeMiddleware,
  createRedactionMiddleware,
  describeError,
  redactSensitiveData,
  type ObservabilityEvent,
  type ObservabilityPipelineOptions,
  type ObservabilitySink,
} from "./observability";

type SentryPrimitive = string | number | boolean;

type SentryScopeLike = {
  setLevel(level: "debug" | "info" | "warning" | "error"): unknown;
  setTags(tags: Record<string, SentryPrimitive>): unknown;
  setContext(name: string, context: Record<string, unknown> | null): unknown;
  setUser(user: { id?: string } | null): unknown;
  setFingerprint(fingerprint: string[]): unknown;
};

/** @sentry/nextjs 외 다른 Sentry SDK도 주입할 수 있는 최소 구조적 계약. */
export type SentryLike = {
  withScope<T>(callback: (scope: SentryScopeLike) => T): T;
  /**
   * SDK v8+에서 top-level `setUser`는 isolation scope에 기록된다. 서버는 요청
   * 단위, 브라우저는 페이지 수명 단위로 유지되므로 이후 자동 수집되는 오류
   * (`onRequestError`·global handler)에도 주체가 따라붙는다.
   */
  setUser(user: { id?: string } | null): unknown;
  captureException(error: unknown): string;
  captureMessage(
    message: string,
    level?: "debug" | "info" | "warning" | "error",
  ): string;
};

export type SentryNextLike = SentryLike & {
  captureRequestError(
    error: unknown,
    request: NextRequestLike,
    context: NextRequestContextLike,
  ): unknown;
};

export type SentrySinkOptions = {
  name?: string;
  shouldCapture?: (event: ObservabilityEvent) => boolean;
};

export type SentryObservabilityOptions = SentrySinkOptions & {
  app: string;
  runtime: string;
  environment?: string;
  release?: string;
  deploymentId?: string;
  region?: string;
  dedupeWindowMs?: number;
};

export type CaptureSentryRequestErrorOptions = {
  app: string;
  runtime: string;
};

export type CaptureBoundaryExceptionOptions = {
  boundary: string;
  browserState?: {
    online: boolean;
    visibilityState: string;
  };
};

type NextRequestLike = {
  method: string;
  path: string;
  headers: Record<string, string | string[] | undefined>;
};

type NextRequestContextLike = {
  routerKind: string;
  routePath: string;
  routeType: string;
  renderSource?: string;
  revalidateReason?: string;
};

const BROWSER_NETWORK_FAILURE_PATTERNS = [
  "Load failed", // Safari
  "Failed to fetch", // Chromium
  "NetworkError when attempting to fetch resource", // Firefox
  "The operation was aborted",
  "The user aborted a request",
  "cancelled",
] as const;

function stringProperty(value: unknown, key: string): string | undefined {
  if (!value || typeof value !== "object" || !(key in value)) return undefined;
  const property = (value as Record<string, unknown>)[key];
  return typeof property === "string" && property.length > 0
    ? property
    : undefined;
}

/**
 * 서버 오류를 가리키는 두 식별자를 **태그**로 뽑는다.
 *
 * - `digest` — 프로덕션 RSC 오류의 유일한 다리. 서버는 실제 메시지를 감추고
 *   클라이언트에는 digest만 내려보내며, 우리 오류 화면도 사용자에게 "오류 코드
 *   {digest}"를 보여준다. 그런데 예전에는 서버가 이 값을 `nextjs` **컨텍스트**에만
 *   넣고 클라이언트 바운더리는 아예 안 붙였다 — 컨텍스트는 Sentry에서 검색이 안 되므로
 *   "사용자가 본 코드"로도, "클라이언트 이벤트"로도 서버 스택을 찾을 수 없었다
 *   (2026-08-02 트리아지에서 REOPT-STUDIO-2의 원인 규명이 여기서 막혔다).
 * - `nextErrorCode` — Next 16이 내부 오류에 붙이는 `__NEXT_ERROR_CODE`(E394·E715 등).
 *   문구가 같아도 원인이 갈리는 오류를 구분해 준다. 예: E715는 새 배포로 사라진
 *   Server Action(= StaleDeploymentRecovery가 이미 처리하는 배포 이벤트), E394는
 *   "RSC가 아닌 응답"(플랫폼 HTML 오류 페이지일 수도, 배포 스큐일 수도)이다.
 *
 * 둘 다 태그라 `digest:abc123`·`nextErrorCode:E394`로 바로 검색·그룹화된다.
 */
function serverErrorIdentityTags(
  error: unknown,
): Record<string, SentryPrimitive> {
  const digest = stringProperty(error, "digest");
  const nextErrorCode = stringProperty(error, "__NEXT_ERROR_CODE");
  return {
    ...(digest ? { digest } : {}),
    ...(nextErrorCode ? { nextErrorCode } : {}),
  };
}

/**
 * React 에러 바운더리(`error.tsx`·`global-error.tsx`)용 캡처.
 *
 * `Sentry.captureException(error)`를 직접 부르면 digest가 이벤트 어디에도 남지 않아
 * 서버 쪽 원인과 이을 수 없다 — 바운더리는 전부 이 함수를 쓴다.
 */
export function captureBoundaryException(
  sentry: SentryLike,
  error: unknown,
  options?: CaptureBoundaryExceptionOptions,
): void {
  const message = error instanceof Error ? error.message : String(error);
  const isNetworkFailure = BROWSER_NETWORK_FAILURE_PATTERNS.some((pattern) =>
    message.includes(pattern),
  );
  const tags = {
    source: "next.error-boundary",
    handled: true,
    ...(options?.boundary ? { boundary: options.boundary } : {}),
    ...(isNetworkFailure ? { browserNetworkFailure: true } : {}),
    ...serverErrorIdentityTags(error),
  };
  sentry.withScope((scope) => {
    scope.setTags(tags);
    if (options?.browserState) {
      scope.setContext("browserState", {
        online: options.browserState.online,
        visibilityState: options.browserState.visibilityState,
      });
    }
    sentry.captureException(error);
  });
}

/**
 * 관측 이벤트의 `attributes`를 Sentry 컨텍스트에 넣기 전에 평탄화한다.
 *
 * `Error` 인스턴스는 열거 가능한 자체 속성이 없어서 그대로 직렬화하면 **빈 객체**가
 * 되고, Sentry 컨텍스트에는 `"[Object]"` 한 줄로만 보인다. 부차 오류를
 * `attributes.error`로 실어 보내던 호출부들이 그래서 원인 없는 이벤트를 남겼다
 * (REOPT-ID-8은 이것 때문에 진단 자체가 불가능했다). EventLog sink는 이미
 * `describeError`로 풀어 담고 있었는데 Sentry sink만 빠져 있었다 — 호출부마다
 * 고치면 다음 호출부에서 또 새므로 sink 한 곳에서 처리한다.
 */
function flattenErrorAttributes(
  attributes: Record<string, unknown>,
): Record<string, unknown> {
  let changed = false;
  const flattened: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(attributes)) {
    if (value instanceof Error) {
      flattened[key] = describeError(value);
      changed = true;
      continue;
    }
    flattened[key] = value;
  }
  return changed ? flattened : attributes;
}

function requestHeader(
  headers: NextRequestLike["headers"],
  name: string,
): string | undefined {
  const value = headers[name] ?? headers[name.toLowerCase()];
  return Array.isArray(value) ? value[0] : value;
}

function toSentryLevel(
  level: ObservabilityEvent["level"],
): "debug" | "info" | "warning" | "error" {
  return level === "warn" ? "warning" : level;
}

/** 공급자 중립 이벤트를 Sentry scope와 Issue로 변환하는 선택적 어댑터. */
export function createSentrySink(
  sentry: SentryLike,
  options: SentrySinkOptions = {},
): ObservabilitySink {
  return {
    name: options.name ?? "sentry",
    levels: ["error"],
    accepts: options.shouldCapture,
    /**
     * isolation scope에 주체를 심어, 이 sink를 지나지 않는 오류까지 커버한다.
     * Next 미처리 요청 오류는 `shouldCapture`가 이 sink에서 제외하고
     * `captureRequestError`가 따로 수집하는데, 그 경로도 같은 isolation scope를
     * 상속하므로 여기서 심은 주체가 그대로 붙는다.
     *
     * PII 정책상 식별자만 보낸다 — 이메일·이름은 넘기지 않고, 필요할 때 이
     * id로 DB에서 조회한다.
     */
    identify(actor) {
      sentry.setUser(actor?.id ? { id: actor.id } : null);
    },
    capture(event) {
      sentry.withScope((scope) => {
        const level = toSentryLevel(event.level);
        scope.setLevel(level);
        scope.setTags({
          source: event.source,
          handled: event.handled,
          ...(event.context ? { loggerContext: event.context } : {}),
          // 오류에 실려 온 digest·Next 오류코드를 검색 가능한 태그로. 명시 tags가
          // 뒤에 오므로 호출부가 준 값이 항상 우선한다.
          ...serverErrorIdentityTags(event.error),
          ...event.tags,
        });

        if (event.actor?.id) scope.setUser({ id: event.actor.id });
        if (event.request) {
          scope.setContext("request", redactSensitiveData(event.request));
        }
        scope.setContext("observability", {
          timestamp: event.timestamp,
          ...(event.actor?.type ? { actorType: event.actor.type } : {}),
          ...(event.attributes
            ? {
                attributes: redactSensitiveData(
                  flattenErrorAttributes(event.attributes),
                ),
              }
            : {}),
        });
        if (event.fingerprint) {
          scope.setFingerprint([...event.fingerprint]);
        }

        if (event.error !== undefined) {
          scope.setContext("errorDetails", describeError(event.error));
          sentry.captureException(event.error);
        } else {
          sentry.captureMessage(event.message, level);
        }
      });
    },
  };
}

/**
 * 앱별 Sentry wiring의 공통 preset. 반환값은 일반 파이프라인 설정이므로 호출자는
 * DB/OTLP sink나 자체 middleware를 배열에 추가해 사용할 수 있다.
 */
export function createSentryObservabilityConfig(
  sentry: SentryLike,
  options: SentryObservabilityOptions,
): Required<Pick<ObservabilityPipelineOptions, "middleware" | "sinks">> {
  return {
    middleware: [
      createEnrichmentMiddleware("runtime-context", {
        tags: {
          app: options.app,
          runtime: options.runtime,
          ...(options.environment ? { environment: options.environment } : {}),
          ...(options.release ? { release: options.release } : {}),
        },
        attributes: {
          ...(options.deploymentId
            ? { deploymentId: options.deploymentId }
            : {}),
          ...(options.region ? { region: options.region } : {}),
        },
      }),
      createRedactionMiddleware(),
      createErrorDedupeMiddleware(options.dedupeWindowMs),
    ],
    sinks: [createSentrySink(sentry, options)],
  };
}

/**
 * Next의 captureRequestError 전용 문맥 보강. 프레임워크 고유 수집은 유지하면서
 * request ID, route, Prisma code를 세 앱에서 동일한 태그로 제공한다.
 */
export function captureSentryRequestError(
  sentry: SentryNextLike,
  error: unknown,
  request: NextRequestLike,
  context: NextRequestContextLike,
  options: CaptureSentryRequestErrorOptions,
): { requestId?: string; digest?: string; prismaCode?: string } {
  const requestId =
    requestHeader(request.headers, "x-request-id") ??
    requestHeader(request.headers, "x-vercel-id");
  const digest = stringProperty(error, "digest");
  const prismaCode = stringProperty(error, "code");

  sentry.withScope((scope) => {
    scope.setTags({
      app: options.app,
      runtime: options.runtime,
      source: "next.request",
      handled: false,
      routeType: context.routeType,
      routerKind: context.routerKind,
      ...(requestId ? { requestId } : {}),
      ...(prismaCode ? { prismaCode } : {}),
      // digest는 컨텍스트에도 남기지만, 클라이언트 이벤트·사용자가 화면에서 읽은
      // 오류 코드와 이으려면 **검색 가능한 태그**여야 한다.
      ...serverErrorIdentityTags(error),
    });
    scope.setContext(
      "request",
      redactSensitiveData({
        id: requestId,
        method: request.method,
        path: request.path,
        routePath: context.routePath,
        routeType: context.routeType,
      }),
    );
    scope.setContext("nextjs", {
      ...(digest ? { digest } : {}),
      ...(context.renderSource ? { renderSource: context.renderSource } : {}),
      ...(context.revalidateReason
        ? { revalidateReason: context.revalidateReason }
        : {}),
    });
    sentry.captureRequestError(error, request, context);
  });

  return { requestId, digest, prismaCode };
}

/**
 * 브라우저에서 "오류처럼 보이지만 조치할 것이 없는" 미처리 rejection들.
 *
 * - fetch 취소 계열: 사용자가 페이지를 떠나거나 모바일 네트워크가 끊기면 진행 중이던
 *   요청이 취소되고, 브라우저마다 다른 문구의 TypeError가 unhandledrejection으로 흐른다.
 *   서버·코드 결함이 아니고 재현·수정 대상도 아니다(Sentry REOPT-STUDIO-5/6).
 * - `UnrecognizedActionError`: 새 배포로 Server Action ID가 바뀐 구 탭. 앱이
 *   `StaleDeploymentRecovery`로 새로고침 안내를 띄워 이미 처리한다.
 */
const BROWSER_NOISE_PATTERNS = [
  ...BROWSER_NETWORK_FAILURE_PATTERNS,
  "UnrecognizedActionError",
] as const;

type SentryExceptionValue = {
  type?: unknown;
  value?: unknown;
  mechanism?: { type?: unknown; handled?: unknown };
};

/**
 * 미처리 rejection(전역 핸들러가 주운 것)이면서 위 문구에 해당할 때만 참.
 *
 * ⚠️ 문구만 보고 버리지 않는 이유: "Failed to fetch"는 CDN 장애·CORS 설정 오류처럼
 * **진짜 고쳐야 하는** 상황에서도 난다. 우리가 `logger`로 명시 캡처한 이벤트와
 * 프레임워크가 보고한 요청 오류는 그대로 남기고, 전역 핸들러가 주운 것만 걸러낸다.
 */
export function isBrowserNoiseEvent(event: unknown): boolean {
  if (!event || typeof event !== "object") return false;
  const values = (event as { exception?: { values?: SentryExceptionValue[] } })
    .exception?.values;
  if (!Array.isArray(values) || values.length === 0) return false;

  return values.some((entry) => {
    const mechanism =
      typeof entry?.mechanism?.type === "string" ? entry.mechanism.type : "";
    const isGlobalHandler =
      mechanism.includes("onunhandledrejection") ||
      mechanism.includes("globalhandlers") ||
      mechanism.includes("global_handlers");
    if (!isGlobalHandler) return false;

    const haystack = `${typeof entry.type === "string" ? entry.type : ""} ${
      typeof entry.value === "string" ? entry.value : ""
    }`;
    return BROWSER_NOISE_PATTERNS.some((pattern) => haystack.includes(pattern));
  });
}

/**
 * 브라우저 Sentry init의 `beforeSend`용 — 잡음은 버리고, 남은 것은 민감정보를 지운다.
 * (서버/엣지는 잡음 성격이 달라 `redactSentryEvent`를 그대로 쓴다.)
 */
export function screenBrowserSentryEvent<T>(event: T): T | null {
  if (isBrowserNoiseEvent(event)) return null;
  return redactSentryEvent(event);
}

/** Sentry SDK의 beforeSend에 직접 연결하는 공통 민감정보 필터. */
export function redactSentryEvent<T>(event: T): T {
  const sanitized = redactSensitiveData(event, {
    maxDepth: 12,
    maxArrayLength: 100,
    maxStringLength: 16_000,
  });
  if (sanitized && typeof sanitized === "object") return sanitized;
  return {
    level: "error",
    message: "Event payload removed because it could not be sanitized",
  } as T;
}
